import _ from 'lodash';
import { DateTime } from 'luxon';

import { Profile } from 'auth/Auth';
import { MetaData } from 'common/response-editor/MetadataParser';
import { hasLinkInMetaData, isValidISOstring, convertDateTimeToCalendarDate } from 'social/common/util/Util';
import {
    ViewState,
    PublisherConfig,
    ReducerAction,
    ComponentType,
    GoogleFields,
    MediaUrlValidation,
    LibraryContent,
    LocationSource,
    LocationUid,
    ScheduleDateObject,
    SchedulePostResponse,
    PostType,
    PublishType,
    ScheduledPost,
    SelectedSource,
    SourceName,
    UrlMetaData,
    PublisherError,
    YoutubeFields,
    SourceEnum,
    ContentPostType,
    MediaType,
    CampaignType,
    ScheduleDateTimeRange,
    CampaignMetaDatum,
    Tag,
    LocationGroup,
    Location,
    FacebookTargetFields,
    LocationTag,
    ScheduledPostState,
    ShareReviewConfig,
    Label,
    PublishSourceType,
    SourceStates,
    SourceState,
    MediaAltText,
    CalendarDateObject,
} from 'social/publisher/Publisher.types';
import { convertTextToContent, getScheduleDateTimeFromPostDateMetaData } from 'social/publisher/util/Util';

const isSourceAvailable = (sourceName: SourceName, locationSources: LocationSource[]): boolean => {
    return locationSources.some((val) => {
        return (
            val.sources[sourceName] === 'VALID' ||
            val.sources[sourceName] === 'INVALID' ||
            val.sources[sourceName] === 0 ||
            val.sources[sourceName] === 1
        );
    });
};

const isLocationUidAvailable = (locationUid: LocationUid, locationSources: LocationSource[]): boolean => {
    return locationSources.some((val) => {
        return val.lUid === locationUid;
    });
};

const getContentMediaPostTypeFromContent = (
    contentData: LibraryContent,
): { content?: MetaData[]; media?: any[]; postType?: PostType } => {
    const { post } = contentData.content?.posts?.[0] || {};
    let result = {};

    if (contentData.content.postType === ContentPostType.TEXT) {
        const text = post.text?.plainText || post.text?.message || '';

        result = {
            content: convertTextToContent(text, post.text?.mentionMetaData, post.text?.hashtagMetaData),
            postType: 'MEDIA',
        };
    } else if (contentData.content.postType === ContentPostType.LINK) {
        const text = post.link?.plainText || post.link?.message || post.link?.linkUrl || '';
        const url = post.link?.linkUrl || '';

        result = {
            content: convertTextToContent(text, post.link?.mentionMetaData, post.text?.hashtagMetaData, url),
            postType: 'LINK',
        };
    } else if (contentData.content.postType === ContentPostType.MEDIA) {
        const text = post.media?.standardMediaPost.plainText || post.media?.standardMediaPost.message || '';
        const mediaUrls = post.media?.standardMediaPost.mediaURLs || [];
        const isMediaVideo = post.media?.standardMediaPost.mediaType === MediaType.SINGLE_VIDEO;
        const isMediaImage =
            post.media?.standardMediaPost.mediaType === MediaType.SINGLE_IMAGE ||
            post.media?.standardMediaPost.mediaType === MediaType.MULTI_IMAGE;
        const media = mediaUrls.map((el, index) => {
            const fileExtension = el.split('.').pop();

            return {
                id: `${contentData.id}-${index}`,
                url: el,
                name: `content-media-${index}`,
                type: `${isMediaImage ? 'image' : 'video'}/${fileExtension}`,
                isVideo: isMediaVideo,
                isImage: isMediaImage,
                size: 20000,
            };
        });

        result = {
            content: convertTextToContent(
                text,
                post.media?.standardMediaPost.mentionMetaData,
                post.media?.standardMediaPost.hashtagMetaData,
            ),
            postType: 'MEDIA',
            media,
        };
    }

    return result;
};

const setContentAndLocationsData = (
    state: ViewState,
    contentData: LibraryContent,
    locationsData: LocationUid[],
): ViewState => {
    const newState = { ...state };
    const { sources } = contentData?.content?.posts?.[0] || [];

    newState.contentId = contentData.id;
    newState.campaignId = contentData.campaignId;
    // set enabled sources for this content lib item
    _.forEach(newState.enabledSources, (v, k) => {
        newState.enabledSources[k] = false;
    });
    // if content sources = [0] (RSS content), enable all sources
    if (sources.indexOf(SourceEnum.ALL) > -1) {
        _.forEach(newState.enabledSources, (v, k) => {
            newState.enabledSources[k] = true;
        });
        // otherwise only enable specified sources
    } else {
        sources.forEach((sourceIndex: number) => {
            const sourceName = SourceEnum[sourceIndex];

            newState.enabledSources[sourceName] = true;
        });
    }
    // set text, link, media, post type
    const contentMediaPostType = getContentMediaPostTypeFromContent(contentData);

    if (contentMediaPostType.content) {
        newState.content = contentMediaPostType.content;
    }
    if (contentMediaPostType.postType) {
        newState.postType = contentMediaPostType.postType;
    }
    if (contentMediaPostType.media) {
        newState.media = contentMediaPostType.media;
    }
    // google fields
    const { content } = contentData;

    if (newState.selectedSources.GOOGLE_PLACES) {
        newState.googleFields = _.pick(content, 'alertType', 'callToAction', 'event');
    }

    // youtube fields
    if (newState.selectedSources.YOUTUBE) {
        newState.youtubeFields = { ...state.youtubeFields };
        if (typeof content.kidsVideo === 'boolean') {
            newState.youtubeFields.kidsVideo = content.kidsVideo;
        }
        if (typeof content.embedVideo === 'boolean') {
            newState.youtubeFields.embedVideo = content.embedVideo;
        }
        if (typeof content.publishToFeed === 'boolean') {
            newState.youtubeFields.publishToFeed = content.publishToFeed;
        }
        if (typeof content.title === 'string') {
            newState.title = content.title;
        }
    }
    // filter cached locationSources by locations associated with this content lib item via campaignId
    newState.locationSources = state.locationSources.filter((el) => locationsData.indexOf(el.lUid) > -1);
    if (newState.locationSources.length === 1) {
        newState.isSingleLocationUser = true;
    }
    // cache data object needed for publishing content
    newState.contentData = _.cloneDeep(contentData);

    if (contentData.labelIds) {
        newState.selectedLabelIds = contentData.labelIds;
    }

    return newState;
};

const setLibraryContentPublishData = (
    state: ViewState,
    payload: { value: { libraryContent: LibraryContent; libraryLocations: LocationUid[] } },
): ViewState => {
    const { libraryContent, libraryLocations } = payload.value;
    const newState = setContentAndLocationsData(state, libraryContent, libraryLocations);

    newState.editorMode = 'CONTENT_PUBLISH';
    newState.campaignType = CampaignType.SHADOW;
    newState.enabledPublishTypes = ['PUBLISH_LATER'];

    return newState;
};

const setCampaignPostPublishData = (
    state: ViewState,
    payload: {
        value: {
            campaignContent: LibraryContent;
            campaignLocations: LocationUid[];
            scheduleDateTimeRange: ScheduleDateTimeRange;
        };
    },
): ViewState => {
    const { campaignContent, campaignLocations, scheduleDateTimeRange } = payload.value;
    const newState = setContentAndLocationsData(state, campaignContent, campaignLocations);

    newState.editorMode = 'CAMPAIGN_PUBLISH';
    newState.campaignType = CampaignType.PUSH;

    // select all locations and lock the selection
    newState.selectedLocations = newState.locationSources.map((el) => el.lUid);
    newState.isLocationsLocked = true;
    newState.isLabelsDisabled = true;

    // lock publish option to publish later
    newState.publishType = 'PUBLISH_LATER';
    newState.isPublishOptionsLocked = true;
    // set min max date based on campaign start/end date
    newState.scheduleDateTimeRange = {
        minDate: scheduleDateTimeRange.minDate,
        maxDate: scheduleDateTimeRange.maxDate,
    };

    return newState;
};

const setCampaignPostCreateData = (
    state: ViewState,
    payload: { value: { campaignData: CampaignMetaDatum; contentData?: LibraryContent } },
): ViewState => {
    const newState: ViewState = { ...state };
    const { campaignData, contentData } = payload.value;

    newState.editorMode = 'CAMPAIGN_CREATE';
    newState.campaignType = CampaignType.PUSH;
    newState.campaignId = campaignData.id;

    _.forEach(newState.enabledSources, (v, k) => {
        newState.enabledSources[k] = k === 'FACEBOOK' || k === 'INSTAGRAM' || k === 'GOOGLE_PLACES';
    });

    newState.isLocationsDisabled = true;
    newState.isAssetsDisabled = true;
    newState.isLabelsDisabled = true;

    // set enabled publish types and default type
    newState.publishType = 'PUBLISH_LATER';
    newState.enabledPublishTypes = ['PUBLISH_LATER', 'MANUAL'];

    // set min max date based on campaign start/end date
    newState.scheduleDateTimeRange = {
        minDate: campaignData.startDate,
        maxDate: campaignData.endDate,
    };

    // if editing existing posts
    if (contentData) {
        const { sources } = contentData?.content?.posts?.[0] || [];

        // set sources
        sources.forEach((source) => {
            const soureName = SourceEnum[source];

            newState.selectedSources[soureName] = true;
        });
        // set text, link, media, post type
        const contentMediaPostType = getContentMediaPostTypeFromContent(contentData);

        if (contentMediaPostType.content) {
            newState.content = contentMediaPostType.content;
        }
        if (contentMediaPostType.postType) {
            newState.postType = contentMediaPostType.postType;
        }
        if (contentMediaPostType.media) {
            newState.media = contentMediaPostType.media;
        }
        // assuming all these date properties are required
        if (contentData.scheduleDate && contentData.scheduleDateObject && contentData.calendarDateObject) {
            newState.scheduleDateTime = [contentData.scheduleDateObject];
        } else {
            newState.publishType = 'MANUAL';
        }
        // cache data object needed for update campaign post call
        newState.contentData = _.cloneDeep(contentData);

        if (newState.selectedSources.GOOGLE_PLACES) {
            newState.googleFields = _.pick(contentData.content, 'alertType', 'callToAction', 'event');
        }
    }

    return newState;
};

const setLibraryContentCreateData = (
    state: ViewState,
    payload: {
        value: {
            libraryContent: LibraryContent | undefined;
            libraryLocations: LocationUid[] | undefined;
            myLocations: Location[];
        };
    },
): ViewState => {
    const { libraryContent, libraryLocations, myLocations } = payload.value;
    const newState = { ...state };

    newState.editorMode = 'CONTENT_CREATE';
    newState.myLocations = [...myLocations];
    newState.isTagsDisabled = false;
    newState.isLabelsDisabled = false;
    newState.isTitleDisabled = false;
    newState.isAssetsDisabled = true;

    // set location groups option and pick default location group
    // potential issue: if a tenant only has 1 location, users with hasContentAddGlobalPermission will see "all locations"
    // note: for users that have access to all locations, we should just show "all locations" and hide "my locations" option, but we don't have a way to know if a user has access to all locations.
    newState.enabledLocationGroups = {
        ALL_LOCATIONS: newState.hasContentAddGlobalPermission,
        MY_LOCATIONS: newState.myLocations.length > 1,
        MY_LOCATION: newState.myLocations.length === 1,
        SELECT_LOCATIONS: newState.myLocations.length > 1,
    };

    if (newState.hasContentAddGlobalPermission) {
        newState.selectedLocationGroup = 'ALL_LOCATIONS';
    } else if (newState.myLocations.length > 1) {
        newState.selectedLocationGroup = 'MY_LOCATIONS';
    } else {
        newState.selectedLocationGroup = 'MY_LOCATION';
    }

    // if creating suggested review content
    if (newState.myLocations.length > 1 && newState.isLocationsLocked && newState.selectedLocations.length > 0) {
        newState.selectedLocationGroup = 'SELECT_LOCATIONS';
    } else {
        newState.isLocationsLocked = false;
    }

    // if editing content
    if (libraryContent && libraryLocations) {
        newState.contentId = libraryContent.id;
        newState.campaignId = libraryContent.campaignId;

        const { sources } = libraryContent.content?.posts?.[0] || [];

        // set sources
        sources.forEach((source) => {
            const soureName = SourceEnum[source];

            newState.selectedSources[soureName] = true;
        });

        if (libraryContent.title) {
            newState.title = libraryContent.title;
        }

        // set text, link, media, post type
        const contentMediaPostType = getContentMediaPostTypeFromContent(libraryContent);

        if (contentMediaPostType.content) {
            newState.content = contentMediaPostType.content;
        }
        if (contentMediaPostType.postType) {
            newState.postType = contentMediaPostType.postType;
        }
        if (contentMediaPostType.media) {
            newState.media = contentMediaPostType.media;
        }

        // google fields
        const { content } = libraryContent;

        if (newState.selectedSources.GOOGLE_PLACES) {
            newState.googleFields = _.pick(content, 'alertType', 'callToAction', 'event');
        }

        // youtube fields
        if (newState.selectedSources.YOUTUBE) {
            newState.youtubeFields = { ...state.youtubeFields };
            if (typeof content.kidsVideo === 'boolean') {
                newState.youtubeFields.kidsVideo = content.kidsVideo;
            }
            if (typeof content.embedVideo === 'boolean') {
                newState.youtubeFields.embedVideo = content.embedVideo;
            }
            if (typeof content.publishToFeed === 'boolean') {
                newState.youtubeFields.publishToFeed = content.publishToFeed;
            }
            if (typeof content.title === 'string') {
                newState.title = content.title;
            }
        }

        if (typeof libraryContent.smartReady === 'boolean') {
            newState.isSmartReady = libraryContent.smartReady;
        }

        // todo: update this once BE supports concepts of ALL VS MY VS Selected locations
        // newState.hasContentAddGlobalPermission
        const myLocationUids = newState.myLocations.map((el) => el.uid);

        if (libraryLocations.length > myLocationUids.length) {
            newState.selectedLocationGroup = 'ALL_LOCATIONS';
        } else if (libraryLocations.length < myLocationUids.length) {
            newState.selectedLocationGroup = 'SELECT_LOCATIONS';
            newState.selectedLocations = [...libraryLocations];
        } else {
            const isLibraryLocationsSameAsMyLocations = _.isEqual(libraryLocations.sort(), myLocationUids.sort());

            if (isLibraryLocationsSameAsMyLocations) {
                newState.selectedLocationGroup = myLocations.length > 1 ? 'MY_LOCATIONS' : 'MY_LOCATION';
            } else {
                newState.selectedLocationGroup = 'SELECT_LOCATIONS';
                newState.selectedLocations = [...libraryLocations];
            }
        }

        if (libraryContent.labelIds) {
            newState.selectedLabelIds = libraryContent.labelIds;
        }

        // cache data object needed for update campaign post call
        newState.contentData = _.cloneDeep(libraryContent);
    }

    return newState;
};

const setLocationSourcesData = (state: ViewState, payload: { value: LocationSource[] }): ViewState => {
    const newState: ViewState = { ...state };

    newState.locationSources = [...payload.value];

    return newState;
};

const setProfileData = (state: ViewState, payload: { value: Profile }): ViewState => {
    const newState: ViewState = { ...state };

    newState.profile = payload.value;

    return newState;
};

const setScheduledPostData = (state: ViewState, payload: { value: ScheduledPost }): ViewState => {
    const newState: ViewState = { ...state };
    const scheduledPost = payload.value;
    const { post } = scheduledPost.postList[0];

    if (!newState.duplicatePost) {
        newState.postId = scheduledPost.id;

        if (scheduledPost.conId) {
            newState.contentId = scheduledPost.conId;
        }
        if (scheduledPost.camId) {
            newState.campaignId = scheduledPost.camId;
        }
        if (scheduledPost.camType) {
            newState.campaignType = scheduledPost.camType;
        }
    }
    // editing post
    if (newState.postId && typeof scheduledPost.multiPublish === 'boolean') {
        newState.duplicatePost = scheduledPost.multiPublish;
    }

    // sources
    // if sources are already selected via config, honor them, this is for retry of specific locations and sources in SP
    // if no sources are selected, select all sources in the SP
    const isAnySourceSelected = _.includes(state.selectedSources, true);

    if (!isAnySourceSelected) {
        const newSourceStates: SourceStates = { ...newState.sourceStates };

        if (scheduledPost.postList) {
            scheduledPost.postList.forEach((p) => {
                if (p.sources) {
                    p.sources.forEach((source) => {
                        newState.selectedSources[source] = true;

                        if (source === 'INSTAGRAM') {
                            if (p.post && p.post.reel) {
                                newState.selectedSources.IG_REELS = true;
                                newState.selectedSources.INSTAGRAM = false;
                            }
                        }

                        const newSourceState: SourceState = {
                            content: newState.content,
                            media: newState.media,
                            postType: newState.postType,
                            urlMetaData: newState.urlMetaData,
                            googleFields: newState.googleFields,
                            youtubeFields: newState.youtubeFields,
                            title: newState.title,
                            isShortenedUrl: newState.isShortenedUrl,
                            hasUserChosenMediaPostType: newState.hasUserChosenMediaPostType,
                            mediaPathsToAltText: newState.mediaPathsToAltText,
                        };

                        // Post type: LINK vs MEDIA
                        if (p.post.link && p.post.link.url) {
                            const {
                                text,
                                link: { url },
                                mentionMetaData,
                                hashtagMetaData,
                            } = p.post;

                            newSourceState.postType = 'LINK';
                            newSourceState.content = convertTextToContent(text, mentionMetaData, hashtagMetaData, url);
                        } else {
                            const { text, mentionMetaData, hashtagMetaData } = p.post;

                            newSourceState.postType = 'MEDIA';
                            newSourceState.content = convertTextToContent(text, mentionMetaData, hashtagMetaData);
                        }
                        if (p.post.mediaPaths && p.post.mediaPaths.length > 0 && scheduledPost.medium) {
                            // Only grab media from 'medium' that belong to this specific post
                            const postMediums = scheduledPost.medium.filter((medium) =>
                                p.post.mediaPaths?.includes(medium.original_url || ''),
                            );

                            newSourceState.media = postMediums.map((el) => {
                                return {
                                    id: el.id,
                                    url: el.original_url,
                                    name: el.public_id && `${el.public_id}`,
                                    type: el.type && el.format ? `${el.type}/${el.format}` : `image/png`,
                                    isVideo: el.type === 'video',
                                    isImage: el.type === 'image',
                                    size: el.bytes,
                                };
                            });
                        }

                        newSourceState.title = p.post.title;

                        newSourceStates[source] = newSourceState;
                    });

                    newState.sourceStates = newSourceStates;
                }
            });

            if (scheduledPost.postList.length > 1) {
                newState.isCustomizingBySource = true;
                // eslint-disable-next-line prefer-destructuring
                newState.customizeSourceKey = scheduledPost.postList[0].sources[0];
            }
        }
    }

    // locations
    newState.oldPostLocations = scheduledPost.locations || [];

    // if locations are already selected via config, honor them, this is for editing or retry a subset of locations in SP
    // if no locations are selected, select all locations in the SP
    if (_.isEmpty(state.selectedLocations)) {
        // for drafts, locations come in as locationUids
        if (scheduledPost.scheduledPostState === ScheduledPostState.DRAFT && scheduledPost.locationUids) {
            newState.selectedLocations = scheduledPost.locationUids;
        } else {
            newState.selectedLocations = scheduledPost.locations?.map((location) => location.uid) || [];
        }
    }

    // Post type: LINK vs MEDIA
    if (post.link && post.link.url) {
        const {
            text,
            link: { url },
            mentionMetaData,
            hashtagMetaData,
        } = post;

        newState.postType = 'LINK';
        newState.content = convertTextToContent(text, mentionMetaData, hashtagMetaData, url);
    } else {
        const { text, mentionMetaData, hashtagMetaData } = post;

        newState.postType = 'MEDIA';
        newState.content = convertTextToContent(text, mentionMetaData, hashtagMetaData);
    }
    if (post.mediaPaths && post.mediaPaths.length > 0 && scheduledPost.medium) {
        // Only grab media from 'medium' that belong to the first post
        const postMediums = scheduledPost.medium.filter((medium) =>
            post.mediaPaths?.includes(medium.original_url || ''),
        );

        newState.media = postMediums.map((el) => {
            return {
                id: el.id,
                url: el.original_url,
                name: el.public_id && `${el.public_id}`,
                type: el.type && el.format ? `${el.type}/${el.format}` : `image/png`,
                isVideo: el.type === 'video',
                isImage: el.type === 'image',
                size: el.bytes,
            };
        });
    }

    if (scheduledPost.postDateMetadataList) {
        newState.scheduleDateTime = getScheduleDateTimeFromPostDateMetaData(scheduledPost.postDateMetadataList);
    } else if (scheduledPost.postDate) {
        newState.scheduleDateTime = [scheduledPost.postDate];
    }

    newState.publishType = 'PUBLISH_LATER';

    if (scheduledPost.sourceToPublishTime) {
        newState.publishType = 'OPTIMAL_TIMES';
        newState.sourceToPublishTime = scheduledPost.sourceToPublishTime;
    }

    if (scheduledPost.scheduledPostState !== ScheduledPostState.DRAFT) {
        newState.enabledPublishTypes = ['PUBLISH_LATER', 'OPTIMAL_TIMES', 'MANUAL'];
    }

    if (newState.selectedSources.GOOGLE_PLACES) {
        newState.googleFields = _.pick(post, 'alertType', 'callToAction', 'event');
    }

    if (newState.selectedSources.YOUTUBE) {
        newState.youtubeFields = { ...state.youtubeFields };
        if (typeof post.kidsVideo === 'boolean') {
            newState.youtubeFields.kidsVideo = post.kidsVideo;
        }
        if (typeof post.embedVideo === 'boolean') {
            newState.youtubeFields.embedVideo = post.embedVideo;
        }
        if (typeof post.publishToFeed === 'boolean') {
            newState.youtubeFields.publishToFeed = post.publishToFeed;
        }
        if (typeof post.title === 'string') {
            newState.title = post.title;
        }
    }
    if (scheduledPost.targetAudienceBySource) {
        if (newState.selectedSources.FACEBOOK) {
            newState.targetAudience.FACEBOOK = scheduledPost.targetAudienceBySource.FACEBOOK;
        }
    }

    if (scheduledPost.dark) {
        if (newState.selectedSources.FACEBOOK) {
            newState.targetAudience.FACEBOOK.isUnpublished = scheduledPost.dark;
        }
    }

    if (scheduledPost.locationTag) {
        newState.locationTag = scheduledPost.locationTag;
    }

    if (scheduledPost.labelIds) {
        newState.selectedLabelIds = scheduledPost.labelIds;
    }

    newState.scheduledPostState = scheduledPost.scheduledPostState;
    newState.isLabelsDisabled = false;
    return newState;
};

const setPublisherConfigData = (state: ViewState, payload: { value: PublisherConfig }): ViewState => {
    const newState: ViewState = { ...state };
    const config = payload.value;

    if (config) {
        if (typeof config.duplicatePost === 'boolean') {
            newState.duplicatePost = config.duplicatePost;
        }
        if (config.postId && typeof config.postId === 'string') {
            if (!newState.duplicatePost) {
                newState.postId = config.postId;
            }
        }
        if (config.contentId && typeof config.contentId === 'string') {
            newState.contentId = config.contentId;
        }
        // Add selected sources, but verify user has at least one location with source available
        if (config.selectedSources && Array.isArray(config.selectedSources) && newState.locationSources.length > 0) {
            config.selectedSources.forEach((source: string) => {
                switch (source) {
                    case 'FACEBOOK':
                        if (isSourceAvailable(source, newState.locationSources)) {
                            newState.selectedSources.FACEBOOK = true;
                        }
                        break;
                    case 'TWITTER':
                        if (isSourceAvailable(source, newState.locationSources)) {
                            newState.selectedSources.TWITTER = true;
                        }
                        break;
                    case 'INSTAGRAM':
                        if (isSourceAvailable(source, newState.locationSources)) {
                            newState.selectedSources.INSTAGRAM = true;
                        }
                        break;
                    case 'GOOGLE_PLACES':
                        // TODO: Enforce single source here?
                        if (isSourceAvailable(source, newState.locationSources)) {
                            newState.selectedSources.GOOGLE_PLACES = true;
                        }
                        break;
                    case 'LINKEDIN':
                        if (isSourceAvailable(source, newState.locationSources)) {
                            newState.selectedSources.LINKEDIN = true;
                        }
                        break;
                    case 'YOUTUBE':
                        if (isSourceAvailable(source, newState.locationSources)) {
                            newState.selectedSources.YOUTUBE = true;
                        }
                        break;
                    case 'IG_REELS':
                        if (isSourceAvailable('INSTAGRAM', newState.locationSources)) {
                            newState.selectedSources.IG_REELS = true;
                        }
                        break;
                    case 'IG_STORIES':
                        if (isSourceAvailable('INSTAGRAM', newState.locationSources)) {
                            newState.selectedSources.IG_STORIES = true;
                        }
                        break;
                    case 'TIKTOK':
                        if (isSourceAvailable(source, newState.locationSources)) {
                            newState.selectedSources.TIKTOK = true;
                        }
                        break;
                    default:
                        console.log('No matching source for ', source);
                }
            });
        }

        // Add selected locations, but verify user has access to the locationUids provided
        if (config.selectedLocations && Array.isArray(config.selectedLocations)) {
            const tempSelectedLocations: LocationUid[] = [];

            config.selectedLocations.forEach((locationUid: LocationUid) => {
                if (isLocationUidAvailable(locationUid, newState.locationSources)) {
                    tempSelectedLocations.push(locationUid);
                }
            });
            newState.selectedLocations = _.uniq(tempSelectedLocations);
        }

        if (config.title && typeof config.title === 'string') {
            newState.title = config.title;
        }

        if (config.content && Array.isArray(config.content)) {
            newState.content = config.content;
            let labels: any[] = [];
            const labelIds: string[] = [];

            config.content.forEach((c) => {
                labels = labels.concat(c.tags);
            });
            labels.forEach((label) => {
                labelIds.push(label?.id);
            });

            newState.selectedLabelIds = labelIds;
        }

        if (config.media && Array.isArray(config.media)) {
            newState.media = [];
            config.media.forEach((mediaItem, index) => {
                if (mediaItem.url) {
                    newState.media.push({
                        url: mediaItem.url,
                        id: mediaItem.id || `media_item_${index}`,
                        name: mediaItem.name || `Media ${index + 1}`,
                        type: mediaItem.type || 'image/png',
                        isVideo: mediaItem.isVideo || false,
                        isImage: mediaItem.isImage || !mediaItem.isVideo || false,
                        size: mediaItem.size || 20000, // TODO: This is just to pass the validation, but actual size may matter.
                    });
                }
            });
        }

        if (config.postType && typeof config.postType === 'string') {
            if (config.content && hasLinkInMetaData(config.content)) {
                if (config.postType === 'LINK') {
                    newState.postType = 'LINK';
                    // TODO: Extract the link from content
                } else if (config.postType === 'MEDIA') {
                    newState.postType = 'MEDIA';
                    newState.hasUserChosenMediaPostType = true;
                }
            }
        }

        // Set the scheduleDateTime date from a best-guessed ISO parsed date
        if (config.publishDate && isValidISOstring(config.publishDate)) {
            const parsedDate = DateTime.fromISO(config.publishDate);

            // This date comes in a string, we'll need to parse out everything manually
            newState.scheduleDateTime = [
                {
                    postDate: convertDateTimeToCalendarDate(parsedDate),
                    // Default to use location timezone no matter what
                    useLocationTimezone: true,
                },
            ];
        }

        // set date time range for publishing schedule
        if (config.scheduleDateTimeRange) {
            const { minDate, maxDate } = config.scheduleDateTimeRange;

            newState.scheduleDateTimeRange = {};
            if (minDate && isValidISOstring(minDate)) {
                newState.scheduleDateTimeRange.minDate = minDate;
            }
            if (maxDate && isValidISOstring(maxDate)) {
                newState.scheduleDateTimeRange.maxDate = maxDate;
            }
        }

        // Set the publishType
        // TODO: some safeguards around the type of publishing depending on editorMode (Content Lib / Campaigns)
        if (config.publishType && typeof config.publishType === 'string') {
            switch (config.publishType) {
                case 'PUBLISH_NOW':
                    newState.publishType = 'PUBLISH_NOW';
                    break;
                case 'PUBLISH_LATER':
                    newState.publishType = 'PUBLISH_LATER';
                    break;
                case 'OPTIMAL_TIMES':
                    newState.publishType = 'OPTIMAL_TIMES';
                    break;
                case 'MANUAL':
                    newState.publishType = 'MANUAL';
                    break;
                case 'DRAFT':
                    newState.publishType = 'DRAFT';
                    break;
                default:
                    newState.publishType = 'PUBLISH_NOW';
            }
        }

        // The url to return to once publishing is complete
        if (typeof config.returnUrl === 'string') {
            newState.returnUrl = config.returnUrl;

            // disable drafts when scheduling assets
            if (config.returnUrl === '/social2/library2/assets' && config.media) {
                newState.enabledPublishTypes = ['PUBLISH_LATER', 'MANUAL'];
            }
        }

        /* Locked Component booleans */
        if (typeof config.isSourcesLocked === 'boolean') {
            newState.isSourcesLocked = config.isSourcesLocked;
        }
        if (typeof config.isLocationsLocked === 'boolean') {
            newState.isLocationsLocked = config.isLocationsLocked;
        }
        if (typeof config.isLocationsDisabled === 'boolean') {
            newState.isLocationsDisabled = config.isLocationsDisabled;
        }
        if (typeof config.isTitleLocked === 'boolean') {
            newState.isTitleLocked = config.isTitleLocked;
        }
        if (typeof config.isTitleDisabled === 'boolean') {
            newState.isTitleDisabled = config.isTitleDisabled;
        }
        if (typeof config.isContentLocked === 'boolean') {
            newState.isContentLocked = config.isContentLocked;
        }
        if (typeof config.isAssetsLocked === 'boolean') {
            newState.isAssetsLocked = config.isAssetsLocked;
        }
        if (typeof config.isAssetsDisabled === 'boolean') {
            newState.isAssetsDisabled = config.isAssetsDisabled;
        }
        if (typeof config.isTagsLocked === 'boolean') {
            newState.isTagsLocked = config.isTagsLocked;
        }
        if (typeof config.isTagsDisabled === 'boolean') {
            newState.isTagsDisabled = config.isTagsDisabled;
        }
        if (typeof config.isLabelsDisabled === 'boolean') {
            newState.isLabelsDisabled = config.isLabelsDisabled;
        }
        if (typeof config.isMediaLocked === 'boolean') {
            newState.isMediaLocked = config.isMediaLocked;
        }
        if (typeof config.isPublishTimeLocked === 'boolean') {
            newState.isPublishTimeLocked = config.isPublishTimeLocked;
        }
        if (typeof config.isPublishOptionsLocked === 'boolean') {
            newState.isPublishOptionsLocked = config.isPublishOptionsLocked;
        }
        if (typeof config.isContentAndMediaLocked === 'boolean') {
            newState.isContentAndMediaLocked = config.isContentAndMediaLocked;
        }
        if (config.postOrigin) {
            newState.postOrigin = config.postOrigin;
            if (config.rating) {
                newState.rating = config.rating;
            }
        }
    }

    return newState;
};

const setPublishType = (state: ViewState, payload: { value: PublishType }): ViewState => {
    const newState: ViewState = { ...state };

    newState.publishType = payload.value;
    return newState;
};

const setIsSingleLocationUser = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.isSingleLocationUser = payload.value;
    return newState;
};

const setHasSocialPermission = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasSocialPermission = payload.value;
    return newState;
};

const setHasApproveAutoPermission = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasApproveAutoPermission = payload.value;
    return newState;
};

const setHasProfilePublishPermission = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasProfilePublishPermission = payload.value;
    return newState;
};

const setHasTagAddPermission = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasTagAddPermission = payload.value;
    return newState;
};

const setHasLabelAddPermission = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasLabelAddPermission = payload.value;
    return newState;
};

const setHasLabelViewPermission = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasLabelViewPermission = payload.value;
    return newState;
};

const setHasContentAddGlobalPermission = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasContentAddGlobalPermission = payload.value;
    return newState;
};

const setHasTargetAudiencePermission = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasTargetAudiencePermission = payload.value;
    return newState;
};

const sethasUserSelectedLocationsManually = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasUserSelectedLocationsManually = payload.value;
    return newState;
};

const setMedia = (state: ViewState, payload: { value: any[] }): ViewState => {
    const newState: ViewState = { ...state };

    newState.media = [...payload.value];

    return newState;
};

const addMedia = (state: ViewState, payload: { value: any }): ViewState => {
    const newState: ViewState = { ...state };
    const newMedia = [...newState.media];

    newMedia.push(payload.value);
    newState.media = newMedia;

    return newState;
};

const removeMedia = (state: ViewState, payload: { value: number }): ViewState => {
    const newState: ViewState = { ...state };
    const newMedia = [...newState.media];

    newMedia.splice(payload.value, 1);
    newState.media = newMedia;

    return newState;
};

const setMediaValidation = (state: ViewState, payload: { value: MediaUrlValidation[] }): ViewState => {
    const newState: ViewState = { ...state };

    newState.mediaValidation = payload.value;
    return newState;
};

const setSelectedLocations = (state: ViewState, payload: { value: LocationUid[] }): ViewState => {
    const newState: ViewState = { ...state };

    // Ensures we don't store duplicate locations
    newState.selectedLocations = _.uniq(payload.value);
    return newState;
};

const setSelectedLocationGroup = (state: ViewState, payload: { value: LocationGroup }): ViewState => {
    const newState: ViewState = { ...state };

    newState.selectedLocationGroup = payload.value;
    return newState;
};

const setScheduleDateTime = (state: ViewState, payload: { value: ScheduleDateObject[] }): ViewState => {
    const newState: ViewState = { ...state };
    const scheduleDateTime = payload.value;

    if (!_.isEmpty(scheduleDateTime)) {
        newState.scheduleDateTime = [...scheduleDateTime];
    } else {
        newState.scheduleDateTime = [];
    }

    return newState;
};

const setSourceToPublishTime = (state: ViewState, payload: { value: { string: CalendarDateObject } }): ViewState => {
    const newState: ViewState = { ...state };
    const sourceToPublishTime = payload.value;

    if (!_.isEmpty(sourceToPublishTime)) {
        newState.sourceToPublishTime = { ...sourceToPublishTime };
    } else {
        newState.sourceToPublishTime = null;
    }

    return newState;
};

const setSelectedSources = (state: ViewState, payload: { value: SelectedSource }): ViewState => {
    const newState: ViewState = { ...state };

    if (payload.value.IG_STORIES) {
        newState.publishSourceType = PublishSourceType.NON_API;
    } else {
        newState.publishSourceType = PublishSourceType.API;
    }

    newState.selectedSources = payload.value;
    newState.isTitleDisabled = !newState.selectedSources.YOUTUBE && newState.editorMode !== 'CONTENT_CREATE';

    // remove source states for sources that are no longer selected
    Object.keys(payload.value).forEach((key) => {
        if (!payload.value[key]) {
            const newSourceStates: SourceStates = { ...newState.sourceStates };

            newSourceStates[key] = null;
            newState.sourceStates = newSourceStates;
        }
    });

    return newState;
};

const setContent = (state: ViewState, payload: { value: MetaData[] }): ViewState => {
    const newState: ViewState = { ...state };

    newState.content = [...payload.value];

    return newState;
};

const setTitle = (state: ViewState, payload: { value: string }): ViewState => {
    const newState: ViewState = { ...state };

    newState.title = payload.value;

    return newState;
};

const setSelectedTags = (state: ViewState, payload: { value: Tag[] }): ViewState => {
    const newState: ViewState = { ...state };

    newState.selectedTags = payload.value;

    return newState;
};

const setSelectedLabels = (state: ViewState, payload: { value: Label[] }): ViewState => {
    const newState: ViewState = { ...state };

    newState.selectedLabels = payload.value;

    return newState;
};

const setIsShowPostSummary = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.isShowPostSummary = payload.value;

    return newState;
};

const setSchedulePostResponse = (state: ViewState, payload: { value: SchedulePostResponse }): ViewState => {
    const newState: ViewState = { ...state };

    newState.schedulePostResponse = payload.value;

    return newState;
};

const setGoogleFields = (state: ViewState, payload: { value: GoogleFields }): ViewState => {
    const newState: ViewState = { ...state };

    newState.googleFields = payload.value;

    return newState;
};

const setYoutubeFields = (state: ViewState, payload: { value: YoutubeFields }): ViewState => {
    const newState: ViewState = { ...state };

    newState.youtubeFields = payload.value;

    return newState;
};

const setUrlMetaData = (state: ViewState, payload: { value: UrlMetaData }): ViewState => {
    const newState: ViewState = { ...state };

    newState.urlMetaData = payload.value;

    return newState;
};

const setPostType = (state: ViewState, payload: { value: PostType }): ViewState => {
    const newState: ViewState = { ...state };

    newState.postType = payload.value;

    return newState;
};

const setHasUserChosenMediaPostType = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.hasUserChosenMediaPostType = payload.value;

    return newState;
};

const setPublisherErrors = (state: ViewState, payload: { value: PublisherError[] }): ViewState => {
    const newState: ViewState = { ...state };

    newState.publisherErrors = payload.value;

    return newState;
};

const removePublisherErrorsByComponent = (state: ViewState, payload: { value: ComponentType }): ViewState => {
    const newState: ViewState = { ...state };

    newState.publisherErrors = newState.publisherErrors.filter((error) => {
        return error.component !== payload.value;
    });

    return newState;
};

const setPromisesInTransit = (state: ViewState, payload: { value: string[] }): ViewState => {
    const newState: ViewState = { ...state };

    newState.promisesInTransit = payload.value;

    return newState;
};

const addPromisesInTransit = (state: ViewState, payload: { value: string }): ViewState => {
    const newState: ViewState = { ...state };

    newState.promisesInTransit.push(payload.value);

    return newState;
};

const removePromisesInTransit = (state: ViewState, payload: { value: string }): ViewState => {
    const newState: ViewState = { ...state };

    // This will remove _all_ of the same values if there's multiple in the array. Note that reducers get called multiple times, so its possible there's dupes in here.
    _.remove(newState.promisesInTransit, (val) => {
        return val === payload.value;
    });

    return newState;
};

const setTargetAudience = (state: ViewState, payload: { value: { FACEBOOK: FacebookTargetFields } }): ViewState => {
    const newState: ViewState = { ...state };

    newState.targetAudience = payload.value;

    return newState;
};

const setDuplicatePostPublishMode = (state: ViewState): ViewState => {
    const newState: ViewState = { ...state };

    newState.duplicatePost = true;
    newState.postId = undefined;
    newState.contentId = null;
    newState.campaignId = null;
    newState.campaignType = null;
    newState.schedulePostResponse = undefined;

    return newState;
};

const setLocationTag = (state: ViewState, payload: { value: LocationTag | null }): ViewState => {
    const newState: ViewState = { ...state };

    newState.locationTag = payload.value;

    return newState;
};

const setIsDMLinkAdded = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.isDMLinkAdded = payload.value;

    return newState;
};

const setIsSmartReady = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.isSmartReady = payload.value;

    return newState;
};

const setShareReviewData = (state: ViewState, payload: { value: ShareReviewConfig }): ViewState => {
    const newState: ViewState = { ...state, ...payload.value };

    if (newState.selectedLocations.length === 0) {
        newState.isLocationsLocked = false;
    }

    return newState;
};

const addLabelIds = (state: ViewState, payload: { value: string[] }): ViewState => {
    const newState: ViewState = { ...state };
    let newLabelIds: string[] = newState.selectedLabelIds ? [...newState.selectedLabelIds] : [];

    newLabelIds = newLabelIds.concat(payload.value);
    newState.selectedLabelIds = newLabelIds;

    return newState;
};

const setCustomizeSource = (state: ViewState, payload: { value: SourceName | 'ALL_SOURCES' }): ViewState => {
    let newState: ViewState = { ...state };
    const prevSourceState: SourceState = {
        content: newState.content,
        media: newState.media,
        postType: newState.postType,
        urlMetaData: newState.urlMetaData,
        googleFields: newState.googleFields,
        youtubeFields: newState.youtubeFields,
        title: newState.title,
        isShortenedUrl: newState.isShortenedUrl,
        hasUserChosenMediaPostType: newState.hasUserChosenMediaPostType,
        mediaPathsToAltText: newState.mediaPathsToAltText,
    };

    // only save old state if source is still selected
    newState.sourceStates[newState.customizeSourceKey] =
        newState.selectedSources[newState.customizeSourceKey] || newState.customizeSourceKey === 'ALL_SOURCES'
            ? prevSourceState
            : null;

    newState.customizeSourceKey = payload.value;

    // overwrite some of the current state with the currently selected source
    if (newState.sourceStates[payload.value]) {
        newState = { ...newState, ...newState.sourceStates[payload.value] };
    } else {
        newState = { ...newState, ...newState.sourceStates.ALL_SOURCES };
    }

    return newState;
};

const setIsCustomizingBySource = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.isCustomizingBySource = payload.value;

    return newState;
};

const setIsShortenedUrl = (state: ViewState, payload: { value: boolean }): ViewState => {
    const newState: ViewState = { ...state };

    newState.isShortenedUrl = payload.value;

    return newState;
};

const setMediaPathToAltText = (state: ViewState, payload: { value: MediaAltText }): ViewState => {
    const newState: ViewState = { ...state };
    const mediaPathsToAltText = newState.mediaPathsToAltText || {};

    mediaPathsToAltText[payload.value.mediaPath] = payload.value.altText;

    newState.mediaPathsToAltText = mediaPathsToAltText;

    return newState;
};

export const publisherReducer = (state: ViewState, action: ReducerAction): ViewState => {
    switch (action.type) {
        /* State actions (actions defined by the UI loading data) */
        case 'set-location-sources-data':
            return setLocationSourcesData(state, action.payload);
        case 'set-profile-data':
            return setProfileData(state, action.payload);
        case 'set-library-content-publish-data':
            return setLibraryContentPublishData(state, action.payload);
        case 'set-library-content-create-data':
            return setLibraryContentCreateData(state, action.payload);
        case 'set-campaign-post-publish-data':
            return setCampaignPostPublishData(state, action.payload);
        case 'set-campaign-post-create-data':
            return setCampaignPostCreateData(state, action.payload);
        case 'set-scheduled-post-data':
            return setScheduledPostData(state, action.payload);
        case 'set-publisher-config-data':
            return setPublisherConfigData(state, action.payload);

        /* Permission actions (actions defined by permissions or available data) */
        case 'set-is-single-location-user':
            return setIsSingleLocationUser(state, action.payload);
        case 'set-has-social-permission':
            return setHasSocialPermission(state, action.payload);
        case 'set-has-approve-auto-permission':
            return setHasApproveAutoPermission(state, action.payload);
        case 'set-has-profile-publish-permission':
            return setHasProfilePublishPermission(state, action.payload);
        case 'set-has-tag-add-permission':
            return setHasTagAddPermission(state, action.payload);
        case 'set-has-label-add-permission':
            return setHasLabelAddPermission(state, action.payload);
        case 'set-has-label-view-permission':
            return setHasLabelViewPermission(state, action.payload);
        case 'set-has-content-add-global-permission':
            return setHasContentAddGlobalPermission(state, action.payload);
        case 'set-has-target-audience-permission':
            return setHasTargetAudiencePermission(state, action.payload);
        /* User actions (actions defined by the user selection or changes) */
        case 'set-publish-type':
            return setPublishType(state, action.payload);
        case 'set-selected-locations':
            return setSelectedLocations(state, action.payload);
        case 'set-selected-location-group':
            return setSelectedLocationGroup(state, action.payload);
        case 'set-schedule-date-time':
            return setScheduleDateTime(state, action.payload);
        case 'set-source-to-publish-time':
            return setSourceToPublishTime(state, action.payload);
        case 'set-selected-sources':
            return setSelectedSources(state, action.payload);
        case 'set-media':
            return setMedia(state, action.payload);
        case 'add-media':
            return addMedia(state, action.payload);
        case 'add-labelIds':
            return addLabelIds(state, action.payload);
        case 'remove-media':
            return removeMedia(state, action.payload);
        case 'set-media-validation':
            return setMediaValidation(state, action.payload);
        case 'set-title':
            return setTitle(state, action.payload);
        case 'set-content':
            return setContent(state, action.payload);
        case 'set-selected-tags':
            return setSelectedTags(state, action.payload);
        case 'set-selected-labels':
            return setSelectedLabels(state, action.payload);
        case 'set-has-user-selected-locations-manually':
            return sethasUserSelectedLocationsManually(state, action.payload);
        case 'set-is-show-post-summary':
            return setIsShowPostSummary(state, action.payload);
        case 'set-schedule-post-response':
            return setSchedulePostResponse(state, action.payload);
        case 'set-google-fields':
            return setGoogleFields(state, action.payload);
        case 'set-youtube-fields':
            return setYoutubeFields(state, action.payload);
        case 'set-url-meta-data':
            return setUrlMetaData(state, action.payload);
        case 'set-has-user-chosen-media-post-type':
            return setHasUserChosenMediaPostType(state, action.payload);
        case 'set-post-type':
            return setPostType(state, action.payload);
        case 'set-publisher-errors':
            return setPublisherErrors(state, action.payload);
        case 'remove-publisher-errors-by-component':
            return removePublisherErrorsByComponent(state, action.payload);
        case 'set-promises-in-transit':
            return setPromisesInTransit(state, action.payload);
        case 'add-promises-in-transit':
            return addPromisesInTransit(state, action.payload);
        case 'remove-promises-in-transit':
            return removePromisesInTransit(state, action.payload);
        case 'set-target-audience':
            return setTargetAudience(state, action.payload);
        case 'set-duplicate-post-publish-mode':
            return setDuplicatePostPublishMode(state);
        case 'set-location-tag':
            return setLocationTag(state, action.payload);
        case 'set-is-dm-link-added':
            return setIsDMLinkAdded(state, action.payload);
        case 'set-share-review-data':
            return setShareReviewData(state, action.payload);
        case 'set-is-smart-ready':
            return setIsSmartReady(state, action.payload);
        case 'set-customize-source':
            return setCustomizeSource(state, action.payload);
        case 'set-is-customizing-by-source':
            return setIsCustomizingBySource(state, action.payload);
        case 'set-is-shortened-url':
            return setIsShortenedUrl(state, action.payload);
        case 'set-media-path-to-alt-text':
            return setMediaPathToAltText(state, action.payload);
        default:
            return state;
    }
};
